import dayjs from 'dayjs';

import {
  detectDateRangePreset,
  getPresetStartDate,
  isValidDateRangePreset,
} from '../utils/datePresets';

interface LabelIdPair {
  label: string;
  id: number;
}

export const VALID_SORT_FIELDS = [
  'updated_at',
  'detected_at',
  'protected_asset_name',
  'company_id',
] as const;
export const VALID_SORT_ORDERS = ['ASCENDING', 'DESCENDING'] as const;

export const parseSortFromParams = (
  params: URLSearchParams,
): { sortBy: string; sortOrder: string } | null => {
  const sortParam = params.get('sort');
  const orderParam = params.get('order');
  if (
    sortParam &&
    orderParam &&
    (VALID_SORT_FIELDS as readonly string[]).includes(sortParam) &&
    (VALID_SORT_ORDERS as readonly string[]).includes(orderParam)
  ) {
    return { sortBy: sortParam, sortOrder: orderParam };
  }
  return null;
};

export const serializeCompanyParam = (companies: LabelIdPair[]): string => {
  return companies.map((c) => `${c.label}:${c.id}`).join(',');
};

export const parseCompanyParam = (param: string): LabelIdPair[] => {
  const pairs = param.split(',').filter(Boolean);
  const resolved: LabelIdPair[] = [];
  for (const pair of pairs) {
    const lastColonIndex = pair.lastIndexOf(':');
    if (lastColonIndex > 0) {
      const name = pair.substring(0, lastColonIndex);
      const id = Number(pair.substring(lastColonIndex + 1));
      if (!isNaN(id) && name) {
        resolved.push({ label: name, id });
      }
    }
  }
  return resolved;
};

export const serializeDateToParams = (
  startDate: string,
  endDate: string,
  defaultStart: string,
  defaultEnd: string,
): Record<string, string> => {
  const result: Record<string, string> = {};

  const resolvedStart = startDate || defaultStart;
  const resolvedEnd = endDate || defaultEnd;

  const preset = detectDateRangePreset(resolvedStart, resolvedEnd);
  if (preset) {
    result.dateRange = preset;
  } else {
    const isDefaultStart = !startDate || startDate === defaultStart;
    const isDefaultEnd = !endDate || endDate === defaultEnd;

    if (!isDefaultStart || !isDefaultEnd) {
      if (!isDefaultStart) {
        result.startDate = resolvedStart;
      }
      if (!isDefaultEnd) {
        result.endDate = resolvedEnd;
      }
    }
  }

  return result;
};

export interface ParsedDateParams {
  startDate: string;
  endDate: string;
}

export const parseDateFromParams = (
  params: URLSearchParams,
  defaultStart: string,
  defaultEnd: string,
): ParsedDateParams => {
  const dateRangeParam = params.get('dateRange');
  const startDateParam = params.get('startDate');
  const endDateParam = params.get('endDate');

  if (dateRangeParam && isValidDateRangePreset(dateRangeParam)) {
    return {
      startDate: getPresetStartDate(dateRangeParam),
      endDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  if (startDateParam || endDateParam) {
    return {
      startDate:
        startDateParam && dayjs(startDateParam).isValid()
          ? startDateParam
          : defaultStart,
      endDate:
        endDateParam && dayjs(endDateParam).isValid()
          ? endDateParam
          : defaultEnd,
    };
  }

  return { startDate: defaultStart, endDate: defaultEnd };
};
